import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { SessionStore } from '../../stores';

interface ProtectedRouteProps {
  children: React.ReactNode;
  masterOnly?: boolean;
  masterUserOnly?: boolean;
}

/**
 * ProtectedRoute - Route wrapper that requires authentication
 * Redirects to /login if user is not authenticated
 * Restores session from stored token.
 *
 * masterOnly checks SessionStore.isAdmin (master users AND group-user admins,
 * role 1) - used for routes group admins should legitimately reach, like
 * /users. masterUserOnly checks SessionStore.isMasterUser only - used for
 * routes whose backend endpoint is master-only, so a group-user admin must
 * never reach it (a 401 there would otherwise log them out).
 */
export const ProtectedRoute = observer(({ children, masterOnly, masterUserOnly }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initSession = async () => {
      if (SessionStore.token && !SessionStore.userData) {
        await SessionStore.fetchSession();
      }
      setIsLoading(false);
    };

    initSession();
  }, []);

  useEffect(() => {
    if (isLoading) return;
    if (!SessionStore.isAuthenticated) {
      navigate('/login', { replace: true });
    } else if (masterOnly && !SessionStore.isAdmin) {
      navigate('/devices', { replace: true });
    } else if (masterUserOnly && !SessionStore.isMasterUser) {
      navigate('/devices', { replace: true });
    }
  }, [isLoading, masterOnly, masterUserOnly, navigate]);

  if (isLoading || !SessionStore.isAuthenticated) {
    return <div>Loading...</div>;
  }

  if (masterOnly && !SessionStore.isAdmin) {
    return null;
  }

  if (masterUserOnly && !SessionStore.isMasterUser) {
    return null;
  }

  return <>{children}</>;
});
